import { Modal } from "antd";
import React, { useState, useEffect } from "react";
import { Controller, useForm } from "react-hook-form";
import Input from "../../../components/input";
import Button from "../../../components/button";
import { Check, ChevronDown, ChevronUp, Plus } from "lucide-react";
import useRequest from "../../../components/hooks/use-request";
import { showToast } from "../../../components/toast";
import { CircleLoader } from "react-spinners";
import Select from "../../../components/select";
import Icon from "../../../assets/icons";

interface Permission {
  resource: string;
  actions: string[];
  _id: string;
}

interface RoleData {
  _id: string;
  name: string;
  permissions: Permission[];
  isActive?: string;
  createdAt?: string;
  updatedAt?: string;
}

interface UserModalProp {
  isVisible: boolean;
  onClose: () => void;
  roleData: RoleData | null;
}

interface ResourcePermissionsType {
  [key: string]: string[];
}

const resourcePermissions: ResourcePermissionsType = {
  sales: ["read", "write", "update", "delete", "approve"],
  inventory: ["read", "write", "update", "delete", "approve"],
  users: ["read", "write", "update", "delete", "approve"],
};

interface RoleFormData {
  name: string;
  permissions: {
    [resource: string]: string[];
  };
}

const EditRole: React.FC<UserModalProp> = ({
  isVisible,
  onClose,
  roleData
}) => {
  const [deleteModal, setDeleteModal] = useState(false);
  const { loading, makeRequest } = useRequest(`/roles/`, "PUT");
  const { loading: deleteLoading, makeRequest: deleteRole } = useRequest(
    `/roles/${roleData?.name}`,
    "DELETE"
  );
  
  // Transform the permissions array into the format expected by the form
  const transformPermissionsToFormFormat = (permissions: Permission[] = []) => {
    const formattedPermissions: { [resource: string]: string[] } = {};
    
    permissions.forEach(permission => {
      formattedPermissions[permission.resource] = permission.actions;
    });
    
    return formattedPermissions;
  };
  
  const { handleSubmit, control, reset, setValue } = useForm<RoleFormData>({
    defaultValues: {
      name: "",
      permissions: {},
    },
  });

  // Update form when roleData changes
  useEffect(() => {
    if (roleData) {
      setValue("name", roleData.name);
      setValue("permissions", transformPermissionsToFormFormat(roleData.permissions));
      
      // Set expanded sections for resources that have permissions
      const initialExpandedSections: { [resource: string]: boolean } = {
        sales: false,
        inventory: false,
        customers: false,
        reports: false,
        users: false,
      };
      
      roleData.permissions.forEach(permission => {
        initialExpandedSections[permission.resource] = true;
      });
      
      setExpandedSections(initialExpandedSections);
    }
  }, [roleData, setValue]);

  const [expandedSections, setExpandedSections] = useState<{
    [resource: string]: boolean;
  }>({
    sales: true, // Default expanded
    inventory: false,
    customers: false,
    reports: false,
    users: false,
  });

  async function handleDeleteRole() {
    if (!roleData?._id) return;
    
    const [response] = await deleteRole();
    if (response.status) {
      showToast(response.message, true, {
        position: "top-right",
      });
      reset();
      onClose();
      window.location.reload();
    } else {
      showToast(response.message, false, {
        position: "top-right",
      });
    }
  }

  const toggleSection = (resource: string) => {
    setExpandedSections({
      ...expandedSections,
      [resource]: !expandedSections[resource],
    });
  };
  
  const isResourceSelected = (
    resource: string,
    permissions: { [resource: string]: string[] }
  ) => {
    return permissions[resource] && permissions[resource].length > 0;
  };

  const toggleResource = (
    resource: string,
    selectedPermissions: { [resource: string]: string[] },
    onChange: Function
  ) => {
    if (isResourceSelected(resource, selectedPermissions)) {
      const newPermissions = { ...selectedPermissions };
      delete newPermissions[resource];
      onChange(newPermissions);
    } else {
      onChange({
        ...selectedPermissions,
        [resource]: ["read"],
      });

      setExpandedSections({
        ...expandedSections,
        [resource]: true,
      });
    }
  };

  const togglePermission = (
    resource: string,
    permission: string,
    selectedPermissions: { [resource: string]: string[] },
    onChange: Function
  ) => {
    const resourcePermissions = selectedPermissions[resource] || [];
    let newResourcePermissions;

    if (resourcePermissions.includes(permission)) {
      newResourcePermissions = resourcePermissions.filter(
        (p) => p !== permission
      );
    } else {
      newResourcePermissions = [...resourcePermissions, permission];
    }

    const newPermissions = {
      ...selectedPermissions,
      [resource]: newResourcePermissions,
    };

    if (newPermissions[resource].length === 0) {
      delete newPermissions[resource];
    }

    onChange(newPermissions);
  };

  const formatPermissionsForSubmission = (permissions: {
    [resource: string]: string[];
  }) => {
    return Object.entries(permissions).map(([resource, actions]) => ({
      resource,
      actions,
    }));
  };

  const handleEditRole = handleSubmit(async (formData) => {
    const hasPermissions = Object.keys(formData.permissions).length > 0;

    if (!hasPermissions) {
      showToast("Please select at least one resource permission", false, {
        position: "top-right",
      });
      return;
    }

    const rolePayload = {
      name: formData.name,
      permissions: formatPermissionsForSubmission(formData.permissions),
    };

    const [response] = await makeRequest(rolePayload);
    if (response.status) {
      showToast(response.message, true, { position: "top-right" });
      reset();
      onClose();
      window.location.reload();
    } else {
      showToast(response?.message || "Role update failed", false, {
        position: "top-right",
      });
    }
  });

  function closeDeleteUserModal() {
    setDeleteModal(false);
  }

  function openDeleteUserModal() {
    setDeleteModal(true);
  }

  return (
    <>
      <Modal
        visible={isVisible}
        onCancel={onClose}
         width="80%"
        centered={true}
        footer={null}
        style={{
          padding: 0,
          margin: 0,
          maxWidth: "1200px",
          height: "auto",
        }}
      >
        <h2 className="text-[#272728] text-[30px] font-semibold">Edit Role</h2>
        <form onSubmit={handleEditRole}>
          <div className="mb-4">
            <label htmlFor="roleName" className="block mb-2">
              Role Name
            </label>
            <Controller
              name="name"
              control={control}
              rules={{ required: "Role name is required" }}
              render={({ field, fieldState: { error } }) => (
                <>
                  <input
                    {...field}
                    id="name"
                    className="w-full rounded-full border p-2 outline-none"
                    placeholder="Enter Role Name"
                  />
                  {error && (
                    <p className="text-red-500 text-sm">{error.message}</p>
                  )}
                </>
              )}
            />
          </div>

          <div className="mb-6">
            <h3 className="mb-3 font-medium">Assign Role Permissions</h3>

            <Controller
              name="permissions"
              control={control}
              render={({ field: { value, onChange } }) => (
                <div className="rounded-lg overflow-hidden">
                  {Object.keys(resourcePermissions).map((resource) => (
                    <div key={resource} className="border-b last:border-b-0">
                      {/* Resource Header */}
                      <div
                        className="flex items-center text-[16px] font-semibold justify-between py-3 px-4 cursor-pointer"
                        onClick={() => toggleSection(resource)}
                      >
                        <div className="flex items-center">
                          <div
                            className={`w-[18px] h-[18px] rounded-md mr-3 border flex items-center justify-center
                            ${
                              isResourceSelected(resource, value)
                                ? "bg-green-600 border-green-600"
                                : "border-gray-400"
                            }`}
                            onClick={(e) => {
                              e.stopPropagation();
                              toggleResource(resource, value, onChange);
                            }}
                          >
                            {isResourceSelected(resource, value) && (
                              <Check
                                size={16}
                                className="text-white rounded-xl"
                              />
                            )}
                          </div>
                          <span className="capitalize font-medium">
                            {resource.charAt(0).toUpperCase() +
                              resource.slice(1)}
                          </span>
                        </div>
                        {expandedSections[resource] ? (
                          <ChevronUp size={20} />
                        ) : (
                          <ChevronDown size={20} />
                        )}
                      </div>

                      {expandedSections[resource] && (
                        <div className="p-3">
                          <div className="grid bg-[#F5F5F5] text-[16px] font-medium grid-cols-5 gap-2 text-center border-b pb-2 mb-2">
                            <div>Read</div>
                            <div>Write</div>
                            <div>Update</div>
                            <div>Delete</div>
                            <div>Approve</div>
                          </div>
                          <div className="grid grid-cols-5 gap-2">
                            {resourcePermissions[resource].map(
                              (permission: any) => (
                                <div
                                  key={permission}
                                  className="flex justify-center"
                                >
                                  <div
                                    className={`w-[18px] h-[18px] rounded-md border flex items-center justify-center cursor-pointer
          ${
            (value[resource] || []).includes(permission)
              ? "bg-green-600 border-green-600"
              : "border-gray-400"
          }`}
                                    onClick={() =>
                                      togglePermission(
                                        resource,
                                        permission,
                                        value,
                                        onChange
                                      )
                                    }
                                  >
                                    {(value[resource] || []).includes(
                                      permission
                                    ) && (
                                      <Check size={16} className="text-white" />
                                    )}
                                  </div>
                                </div>
                              )
                            )}
                          </div>
                        </div>
                      )}
                    </div>
                  ))}
                </div>
              )}
            />

            <Controller
              name="permissions"
              control={control}
              render={({ field: { value } }) => (
                <>
                  {Object.keys(value).length === 0 && (
                    <p className="text-red-500 text-sm mt-2">
                      Please select at least one resource permission
                    </p>
                  )}
                </>
              )}
            />
          </div>

          <div className="flex flex-col justify-end gap-y-3 space-x-2 mt-14">
            <Button
              size="lg"
              variant="tertiary"
              type="button"
              className="font-normal"
              onClick={openDeleteUserModal}
            >
              {deleteLoading ? (
                <CircleLoader
                  color="#ffffff"
                  loading={deleteLoading}
                  size={20}
                />
              ) : (
                <div className="flex gap-3">
                  <Icon name="deleteIcon" /> Delete Role
                </div>
              )}
            </Button>

            <Button
              size="lg"
              variant="primary"
              type="submit"
              className="font-normal"
            >
              {loading ? (
                <CircleLoader color="#ffffff" loading={loading} size={20} />
              ) : (
                <div className="flex gap-3">
                  <Icon name="editIcon" /> Edit Role
                </div>
              )}
            </Button>
          </div>
        </form>
      </Modal>

      <Modal
        visible={deleteModal}
        onCancel={closeDeleteUserModal}
        width={560}
        footer={null}
        closable={true}
        closeIcon={<span className="text-gray-500 text-lg">&times;</span>}
      >
        <div className="py-4">
          <div className="flex gap-4 items-start mb-4">
            <Icon name="warningIcon" />
            <div>
              <h2 className=" font-semibold text-[#1A1A1A] text-[16px] ">
                Delete Role
              </h2>
              <p className="text-[#4E4E4E] text-[14px]">
                Are you sure you want to delete Role "{roleData?.name}"
              </p>
            </div>
          </div>

          <div className="flex justify-start gap-4 mt-6">
            <button
              className="bg-[#D97706] w-[117px] h-[40px] hover:bg-amber-600 rounded-full text-white"
              onClick={handleDeleteRole}
            >
              Yes, Continue
            </button>
            <button
              className="bg-white w-[117px] h-[40px] text-[#4E4E4E] rounded-full border-2 font-medium border-[#9D9898]"
              onClick={closeDeleteUserModal}
            >
              No, Go back
            </button>
          </div>
        </div>
      </Modal>
    </>
  );
};

export default EditRole;